import posthog from 'posthog-js';
import { useNavigate } from 'react-router';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import useReplyToClientActionsStore from '@/features/compose/stores/useReplyToClientActionsStore';
import type { ConceptReplyRequest } from '@/features/compose/types';
import {
  createMeetingFromAction,
  openMeetingRequest,
} from '@/features/email/outlook/meeting';
import useAuth from '@/hooks/useAuth';
import transformDeeplink from '@/lib/deeplink';
import paths from '@/lib/paths';
import type { Action, ActionType } from '@/types/actions';

import useSelectedParty from './useSelectedParty';
import useSetReminder from './useSetReminder';

const NAVIGATION_ACTION_TYPES: Array<ActionType> = [
  // Does not exist in sdk
  // 'REQUEST_MISSING_INFO',
  'UPDATE_POLICY',
  'RECALCULATE_POLICY',
  'CALCULATE_QUOTE',
  'NAVIGATE_LINK',
  // Does not exist in sdk
  // 'CREATE_ADVISORY_REPORT',
  'SEND_RISK_ANALYSIS',
  'SEND_QUICKSCAN',
  'RUN_POLICY_TERMS_CHECK',
  'CALL_CUSTOMER',
];

const EMAIL_ACTION_TYPES: Array<ActionType> = [
  // Does not exist in sdk
  // 'REQUEST_MISSING_INFO'
];

const INTERACTIVE_ACTIONS: Set<ActionType> = new Set([
  ...NAVIGATION_ACTION_TYPES,
  ...EMAIL_ACTION_TYPES,
  'SCHEDULE_MEETING',
  'SET_REMINDER',
  'REPLY_TO_CLIENT',
]);

const NOT_IMPLEMENTED: Set<ActionType> = new Set(['RETRIEVE_INFO']);

const getActionTo = (action: Action) => {
  if (!NAVIGATION_ACTION_TYPES.includes(action.type)) return undefined;

  if (action.url?.startsWith('deeplink:')) return transformDeeplink(action.url);

  return action.url ?? undefined;
};

export default function useAction(action: Action) {
  const {
    email,
    analysis: { analysis },
  } = useEmail();

  const { actions } = useReplyToClientActionsStore();

  const to = getActionTo(action);
  const isCompleted = false;

  const isClickable = (() => {
    if (!INTERACTIVE_ACTIONS.has(action.type)) return false;
    if (NOT_IMPLEMENTED.has(action.type)) return false;

    if (NAVIGATION_ACTION_TYPES.includes(action.type) && to === undefined)
      return false;

    return true;
  })();

  const { distribution } = useAuth();

  const party = useSelectedParty();

  const scheduleMeeting = () => {
    const meetingOptions = createMeetingFromAction(
      action,
      party,
      distribution?.address,
    );

    openMeetingRequest(meetingOptions);
  };

  const setReminder = useSetReminder(action);

  const navigate = useNavigate();

  const handleReplyToClient = () => {
    actions.updateActions(email?.messageId ?? '', [action.id]);

    const request: ConceptReplyRequest = {
      actions: [action.id],
      source: 'analysis-action',
    };

    navigate(paths.conceptReply, { state: request });
  };

  const trackClick = () => {
    posthog.capture('Analysis action clicked', {
      actionType: action.type,
      source: action.source,
      actionId: action.id,
      messageId: email?.messageId,
      emailId: analysis?.email_id,
      partyId: party?.id,
    });
  };

  const handleActionClick = (): Promise<void> | void => {
    trackClick();

    switch (action.type) {
      case 'SCHEDULE_MEETING':
        return scheduleMeeting();
      case 'SET_REMINDER':
        return setReminder();
      case 'REPLY_TO_CLIENT':
        return handleReplyToClient();
      default:
        console.debug(`Not implemented: ${action.type}`);
        break;
    }
  };

  return {
    isCompleted,
    isClickable,
    handleActionClick,
    trackClick,
    to,
  };
}
